import React, { useState } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import Button from '../Button/Button'
import Heading from '../Heading/Heading'
import TextField from '../TextField/TextField'
import Wrapper from '../common/Wrapper'
import { Typography } from '@material-ui/core'
import { useTranslation } from 'react-i18next'

const useStyles = makeStyles((theme) => ({
  heading: {
    marginBottom: theme.spacing(2)
  },
  errorMessage: {
    color: theme.palette.error.main
  }
}))

export interface Props {
  /**
   * What headline to use.
   */
  headline?: string
  /**
   * What subtitle to use.
   */
  subtitle?: string
  /**
   * An array of action components, for example a button
   */
  actionComponents?: React.ReactNode[]
  /**
   * An array of content components, for example a input
   */
  contentComponents?: React.ReactNode[]
  /**
   * Callback fired when submit button is clicked.
   *
   * @param {string} user Value for login.
   * @param {string} password Password for login.
   */
  onSubmit?: (user: string, password: string) => void
  /**
   * Callback fired when 'Forgot password' button is clicked.
   *
   * @param {string} user Value required for login.
   */
  onForgottenPassword?: (user: string) => void
  /**
   * The value of the component, required for a controlled component.
   */
  userValue?: string
  /**
   * Callback fired when the value is changed.
   *
   * @param {object} event The event source of the callback.
   * You can pull out the new value by accessing `event.target.value`.
   */
  userOnChange?: (e: React.ChangeEvent<HTMLInputElement>) => void
  /**
   * The 'disabled' attribute for the button elements. If 'true', buttons will be disabled.
   */
  buttonDisabled?: boolean
  /**
   * What error text to use.
   */
  errorText?: string
  /**
   * Style attribute
   */
  style?: React.CSSProperties
  /**
   * Class attribute
   */
  className?: string
  /**
   * Determine the max-width of the component.
   * The component width grows with the size of the screen.
   * Set to `false` to disable `maxWidth`.
   */
  maxWidth?: 'lg' | 'md' | 'sm' | 'xl' | 'xs' | false
}

interface LoginSubComponents {
  ForgotPassword: React.FC<Props>
  EmailOTP: React.FC<Props>
}

const Login: React.FC<Props> & LoginSubComponents = ({
  headline,
  subtitle,
  actionComponents,
  contentComponents,
  onSubmit,
  onForgottenPassword,
  userValue,
  buttonDisabled = false,
  errorText = '',
  style,
  className,
  maxWidth
}) => {
  const classes = useStyles()
  const { t } = useTranslation(['Login'])
  const [user, setUser] = useState<string>(userValue || '')
  const [password, setPassword] = useState<string>('')
  const [passwordVisible, setPasswordVisible] = useState<boolean>(false)
  const [showForgotPassword, setShowForgotPassword] = useState<boolean>(false)

  if (showForgotPassword) {
    const forgotPassword = () => {
      onForgottenPassword!(user)
    }
    return (
      <Login.ForgotPassword
        userValue={user}
        userOnChange={(e) => setUser(e.target.value)}
        onSubmit={forgotPassword}
        buttonDisabled={buttonDisabled}
        errorText={errorText}
        style={style}
        className={className}
        maxWidth={maxWidth}
      />
    )
  }

  const submit = (e: React.FormEvent) => {
    e.preventDefault()
    if (onSubmit) {
      onSubmit(user, password)
    }
  }

  return (
    <Wrapper style={style} className={className} maxWidth={maxWidth}>
      <form onSubmit={submit}>
        <Heading
          className={classes.heading}
          align="center"
          headline={headline || t('headline')}
          subtitle={subtitle || t('subtitle')}
        />
        {!contentComponents ? (
          <React.Fragment>
            <TextField
              name="user"
              autoFocus
              fullWidth
              placeholder={t('primaryTextField')}
              prefix={{ icon: 'MailOutline' }}
              value={user}
              onChange={(e) => setUser(e.target.value)}
              errorText={errorText ? ' ' : ''}
            />
            <TextField
              name="password"
              fullWidth
              placeholder={t('secondaryTextField')}
              type={passwordVisible ? 'text' : 'password'}
              prefix={{ icon: 'LockOutlined' }}
              suffix={{
                icon: passwordVisible ? 'Visibility' : 'VisibilityOff',
                onClick: () => setPasswordVisible(!passwordVisible)
              }}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              errorText={errorText ? ' ' : ''}
            />
          </React.Fragment>
        ) : contentComponents ? (
          contentComponents.map((content, index) => {
            return <div key={index}>{content}</div>
          })
        ) : null}
        <Typography
          align="center"
          className={classes.errorMessage}
          variant="body2"
          style={{ visibility: errorText ? 'visible' : 'hidden' }}
        >
          {errorText || 'Error'}
        </Typography>
        {!actionComponents ? (
          <div>
            <Button
              fullWidth
              uppercase
              color="primary"
              type="submit"
              disabled={buttonDisabled}
              label={t('primaryButton')}
            />
            {onForgottenPassword && (
              <Button
                fullWidth
                variant="text"
                color="primary"
                disabled={buttonDisabled}
                label={t('secondaryButton')}
                onClick={() => setShowForgotPassword(true)}
              />
            )}
          </div>
        ) : (
          actionComponents.map((actionComponent, index) => {
            return <div key={index}>{actionComponent}</div>
          })
        )}
      </form>
    </Wrapper>
  )
}

const ForgotPassword: React.FC<Props> = ({
  headline,
  subtitle,
  actionComponents,
  contentComponents,
  onSubmit,
  userValue,
  userOnChange,
  buttonDisabled = false,
  errorText = '',
  style,
  className,
  maxWidth
}) => {
  const { t } = useTranslation(['LoginForgotPassword'])

  return (
    <Login
      headline={headline || t('headline')}
      subtitle={subtitle || t('subtitle')}
      contentComponents={
        contentComponents || [
          <TextField
            name="email"
            autoFocus
            fullWidth
            placeholder={t('primaryTextField')}
            prefix={{ icon: 'Email' }}
            value={userValue}
            onChange={userOnChange}
            errorText={errorText ? ' ' : ''}
          />
        ]
      }
      actionComponents={
        actionComponents || [
          <Button
            type="submit"
            color="primary"
            uppercase
            fullWidth
            label={t('primaryButton')}
          />
        ]
      }
      onSubmit={onSubmit}
      userValue={userValue}
      buttonDisabled={buttonDisabled}
      errorText={errorText}
      style={style}
      className={className}
      maxWidth={maxWidth}
    />
  )
}
Login.ForgotPassword = ForgotPassword

const EmailOTP: React.FC<Props> = ({
  headline,
  subtitle,
  actionComponents,
  contentComponents,
  onSubmit,
  userValue,
  userOnChange,
  buttonDisabled = false,
  errorText = '',
  style,
  className,
  maxWidth
}) => {
  const { t } = useTranslation(['LoginOTP'])

  return (
    <Login
      headline={headline || t('headline')}
      subtitle={subtitle || t('subtitle')}
      contentComponents={
        contentComponents || [
          <TextField
            name="email"
            autoFocus
            fullWidth
            placeholder={t('primaryTextField')}
            prefix={{ icon: 'Email' }}
            value={userValue}
            onChange={userOnChange}
            errorText={errorText ? ' ' : ''}
          />
        ]
      }
      actionComponents={
        actionComponents || [
          <Button
            type="submit"
            color="primary"
            uppercase
            fullWidth
            label={t('primaryButton')}
          />
        ]
      }
      onSubmit={onSubmit}
      userValue={userValue}
      userOnChange={userOnChange}
      buttonDisabled={buttonDisabled}
      errorText={errorText}
      style={style}
      className={className}
      maxWidth={maxWidth}
    />
  )
}
Login.EmailOTP = EmailOTP

export default Login
